import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { mergeOrders } from '../../../../api/orderItemsApi';
import { getApiErrorText } from '../../../../utils/apiResponse';
import { getOrderMergeRules } from '../../../../utils/orderMergeRules';
import AdminOrderSummary from './AdminOrderSummary';

export default function MergeOrdersModal({ orderIds, onClose, onMerged }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [reload, setReload] = useState(0);
  const [riskStep, setRiskStep] = useState(false);
  const [riskConfirmed, setRiskConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setLoading(true); setLoadError(''); setOrders([]);
    setRiskConfirmed(false); setRiskStep(false);
    Promise.all(orderIds.map(id => axios.get(`/api/v1/order/${id}`, {
      headers: { Authorization: localStorage.getItem('token') }, signal: controller.signal,
    }))).then(responses => {
      const result = responses.map(response => response.data?.data);
      if (result.some(order => !order?.orderId)) throw new Error('Одне із замовлень не знайдено');
      if (current) setOrders(result);
    }).catch(error => {
      if (current) setLoadError(getApiErrorText(error, 'Не вдалося завантажити замовлення'));
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [orderIds, reload]);

  const { riskOrders, blockedOrders, unavailableOrders } = getOrderMergeRules(orders);
  const mergeBlocked = loading || Boolean(loadError) || orderIds.length < 2 ||
    blockedOrders.length > 0 || unavailableOrders.length > 0;
  const targetId = Math.max(...orderIds);
  const submit = async event => {
    event.preventDefault();
    if (savingLock.current || mergeBlocked) return;
    if (riskOrders.length && !riskStep) { setRiskStep(true); return; }
    if (riskOrders.length && !riskConfirmed) return;
    savingLock.current = true; setSaving(true); setSaveError('');
    try {
      const response = await mergeOrders(orderIds, riskOrders.length > 0 && riskConfirmed);
      if (mounted.current) onMerged(response.data.data.targetOrderId);
    } catch (error) {
      if (mounted.current) {
        setSaveError(getApiErrorText(error, 'Не вдалося об’єднати замовлення'));
        setReload(value => value + 1);
      }
    } finally {
      savingLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Modal show onHide={() => !savingLock.current && onClose()} backdrop='static' keyboard={!saving}
      centered aria-labelledby='merge-orders-title'>
      <form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title as='h2' id='merge-orders-title' className='h5 font-weight-bold mb-0'>
            {riskStep ? 'Підтвердіть ризик виплати Prom' : 'Об’єднати замовлення'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <p role='status'>Завантаження замовлень…</p>}
          {loadError && <div className='alert alert-danger' role='alert'>{loadError}</div>}
          {!loading && !loadError && (riskStep ? (
            <>
              <div className='alert alert-warning' role='alert'>
                <strong className='d-block mb-2'>Можливі проблеми з виплатою оплати за ці замовлення.</strong>
                <p>Об’єднання замовлень із Пром-оплатою або оплатою частинами може порушити зв’язок
                  між оплатою, замовленням і відправленням на Prom. Робіть це лише якщо впевнені,
                  що виплату буде отримано коректно.</p>
                <ul className='mb-0'>
                  {riskOrders.map(order => <li key={order.orderId}>
                    #{order.orderId} — {order.paymentType?.adminValue || order.paymentType?.clientValue}
                  </li>)}
                </ul>
              </div>
              <label className='d-flex align-items-start mb-0'>
                <input type='checkbox' className='mt-1 mr-2' checked={riskConfirmed} disabled={saving}
                  onChange={event => setRiskConfirmed(event.target.checked)} />
                Я розумію ризик для виплати та впевнений, що ці замовлення можна об’єднати.
              </label>
            </>
          ) : (
            <>
              <h3 className='h6 font-weight-bold'>Обрані замовлення</h3>
              <ul className='list-unstyled mb-3' style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {orders.map(order => <li key={order.orderId} className='border rounded p-3 mb-2'>
                  <AdminOrderSummary order={order} />
                  <div className='small mt-1'>Оплата: {order.paymentType?.adminValue || order.paymentType?.clientValue || 'Не визначено'}</div>
                </li>)}
              </ul>
              <div className='alert alert-info d-flex small mb-0' role='note'>
                <i className='bi bi-info-circle mr-2' aria-hidden='true' />
                <div>
                  Товари та прив’язані оплати перейдуть до замовлення <strong>#{targetId}</strong>.
                  {' '}Вихідні Prom-замовлення залишаться нульовими зі статусом «Скасовано» та поясненням
                  в адмінкоментарі, щоб не імпортуватися повторно. Порожні звичайні замовлення буде видалено.
                </div>
              </div>
              {orders.some(order => order.promoCode) && <p className='small text-muted mt-3'>Промокод буде застосовано один раз до об’єднаного замовлення. Якщо коди або збережені умови різні, спочатку приберіть зайві промокоди в деталях замовлень.</p>}
              {blockedOrders.length > 0 && <div className='alert alert-danger mt-3 mb-0' role='alert'>
                Замовлення Prom {blockedOrders.map(order => `#${order.orderId}`).join(', ')} мають
                непідтримуваний спосіб оплати. Дозволена оплата при отриманні або Пром-оплата / оплата
                частинами після підтвердження ризику.
              </div>}
              {unavailableOrders.length > 0 && <div className='alert alert-danger mt-3 mb-0' role='alert'>
                Замовлення {unavailableOrders.map(order => `#${order.orderId}`).join(', ')} вже відправлені,
                скасовані або повернені. Їх не можна об’єднати. Приберіть їх із вибору або спочатку
                відновіть потрібний робочий статус.
              </div>}
            </>
          ))}
          {saveError && <div className='alert alert-danger mt-3 mb-0' role='alert'>{saveError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={onClose} disabled={saving}>Скасувати</button>
          {riskStep && <button type='button' className='btn btn-outline-secondary' disabled={saving}
            onClick={() => { setRiskStep(false); setRiskConfirmed(false); }}>Назад</button>}
          <button type='submit' className={`btn ${riskStep ? 'btn-warning' : 'btn-dark'}`}
            disabled={mergeBlocked || saving || (riskStep && !riskConfirmed)}>
            {saving ? 'Об’єднання…' : riskStep ? 'Об’єднати попри ризик' : riskOrders.length ? 'Продовжити' : 'Об’єднати'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
